/**
 * Goal Create/Edit Sheet — bottom sheet with form sections.
 * Follows habit-form-sheet.tsx pattern exactly.
 */

import React, { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Milestone, Settings2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useGoalsStore } from '@turnifly/shared/stores'
import { useAuthStore } from '@turnifly/shared/stores'
import { useCreateGoal, useUpdateGoal, useAnnounceGoal } from '@turnifly/shared/hooks'
import { generateAutoMilestones } from '@turnifly/shared/utils'
import { AccordionSection } from '../habit-form/accordion-section'
import { GoalFormBasic } from './goal-form-basic'
import { GoalFormType } from './goal-form-type'
import { GoalFormMilestones } from './goal-form-milestones'
import { GoalFormAdvanced } from './goal-form-advanced'
import { GoalAnnounceModal } from './goal-announce-modal'
import type { Goal, GoalStep } from '@turnifly/shared/types'

type OpenSection = 'milestones' | 'advanced' | null

export const GoalCreateSheet: React.FC = () => {
  const { t } = useTranslation()
  const { isCreateSheetOpen, editingGoal, closeCreate } = useGoalsStore()
  const { user } = useAuthStore()
  const createGoal = useCreateGoal()
  const updateGoal = useUpdateGoal()
  const announceGoalMutation = useAnnounceGoal()

  const isEditMode = !!editingGoal

  // ── Form state ──
  const [goalType, setGoalType] = useState<'numeric' | 'steps'>('numeric')
  const [steps, setSteps] = useState<GoalStep[]>([])
  const [icon, setIcon] = useState('🎯')
  const [name, setName] = useState('')
  const [category, setCategory] = useState('')
  const [year, setYear] = useState(new Date().getFullYear())
  const [targetValue, setTargetValue] = useState('')
  const [unit, setUnit] = useState('')
  const [direction, setDirection] = useState<'increase' | 'decrease'>('increase')
  const [isPrivate, setIsPrivate] = useState(false)
  const [description, setDescription] = useState('')
  const [startValue, setStartValue] = useState('')
  const [deadline, setDeadline] = useState('')
  const [monthlyReminder, setMonthlyReminder] = useState(false)
  const [announceGoalTarget, setAnnounceGoalTarget] = useState<Goal | null>(null)
  const [autoMilestones, setAutoMilestones] = useState(true)
  const [weeklyMilestones, setWeeklyMilestones] = useState(false)
  const [manualTargets, setManualTargets] = useState<Array<{ month: number; target: number }>>([])
  const [openSection, setOpenSection] = useState<OpenSection>(null)
  const [error, setError] = useState<string | null>(null)

  // ── Pre-populate on edit ──
  useEffect(() => {
    if (editingGoal) {
      setGoalType(editingGoal.goal_type ?? 'numeric')
      setSteps(editingGoal.steps ?? [])
      setIcon(editingGoal.icon)
      setName(editingGoal.name)
      setCategory(editingGoal.category ?? '')
      setYear(editingGoal.year)
      setTargetValue(String(editingGoal.target_value))
      setUnit(editingGoal.unit)
      setDirection(editingGoal.direction)
      setIsPrivate(editingGoal.is_private)
      setDescription(editingGoal.description ?? '')
      setStartValue(editingGoal.start_value > 0 ? String(editingGoal.start_value) : '')
      setDeadline(editingGoal.deadline ?? '')
      setMonthlyReminder(editingGoal.monthly_reminder)
      setAutoMilestones(editingGoal.auto_milestones)
      setWeeklyMilestones(editingGoal.weekly_milestones)
    } else {
      resetForm()
    }
  }, [editingGoal])

  const resetForm = useCallback((): void => {
    setGoalType('numeric')
    setSteps([])
    setIcon('🎯')
    setName('')
    setCategory('')
    setYear(new Date().getFullYear())
    setTargetValue('')
    setUnit('')
    setDirection('increase')
    setIsPrivate(false)
    setDescription('')
    setStartValue('')
    setDeadline('')
    setMonthlyReminder(false)
    setAutoMilestones(true)
    setWeeklyMilestones(false)
    setManualTargets([])
    setOpenSection(null)
    setError(null)
  }, [])

  const handleClose = useCallback((): void => {
    closeCreate()
    resetForm()
  }, [closeCreate, resetForm])

  const handleManualTargetChange = useCallback((month: number, value: number): void => {
    setManualTargets((prev) => {
      const filtered = prev.filter((m) => m.month !== month)
      if (value > 0) filtered.push({ month, target: value })
      return filtered.sort((a, b) => a.month - b.month)
    })
  }, [])

  const toggleSection = useCallback((section: OpenSection): void => {
    setOpenSection((prev) => (prev === section ? null : section))
  }, [])

  // ── Submit ──
  const handleSubmit = useCallback((): void => {
    if (!name.trim()) {
      setError(t('goals.name') + ' — required')
      return
    }

    // Steps mode validation
    if (goalType === 'steps') {
      const validSteps = steps.filter((s) => s.label.trim())
      if (validSteps.length === 0) {
        setError(t('goals.stepsRequired'))
        return
      }
    }

    // Numeric mode validation
    if (goalType === 'numeric') {
      const tvNum = Number(targetValue)
      if (!tvNum || tvNum <= 0) {
        setError(t('goals.targetValue') + ' — required')
        return
      }
      if (!unit.trim()) {
        setError(t('goals.unit') + ' — required')
        return
      }
    }
    if (!user?.id) return

    setError(null)

    const isStepsMode = goalType === 'steps'
    const validSteps = steps.filter((s) => s.label.trim()).map((s) => ({ label: s.label.trim() }))
    const tv = isStepsMode ? validSteps.length : (Number(targetValue) || 1)

    const milestones = isStepsMode
      ? []
      : autoMilestones
        ? generateAutoMilestones(tv, new Date().getMonth() + 1)
        : manualTargets.map((m) => ({ ...m, achieved: 0 }))

    const payload = {
      name: name.trim(),
      icon,
      goal_type: goalType,
      category: category || null,
      description: description.trim() || null,
      cover_image_url: null,
      year,
      target_value: tv,
      start_value: isStepsMode ? 0 : (direction === 'decrease' ? (Number(startValue) || 0) : 0),
      unit: isStepsMode ? 'qadam' : unit.trim(),
      direction: isStepsMode ? 'increase' as const : direction,
      steps: isStepsMode ? validSteps : null,
      milestones,
      auto_milestones: isStepsMode ? false : autoMilestones,
      weekly_milestones: isStepsMode ? false : weeklyMilestones,
      status: 'planned' as const,
      is_private: isPrivate,
      is_announced: false,
      monthly_reminder: monthlyReminder,
      deadline: deadline || null,
      sort_order: 0,
    }

    if (isEditMode && editingGoal) {
      // Don't overwrite status, sort_order, or is_announced on edit
      const { status: _s, sort_order: _so, is_announced: _ia, ...editPayload } = payload
      updateGoal.mutate(
        { goalId: editingGoal.id, updates: editPayload },
        {
          onSuccess: handleClose,
          onError: (err) => setError(err instanceof Error ? err.message : t('common.error')),
        },
      )
    } else {
      createGoal.mutate(payload, {
        onSuccess: (newGoal) => {
          closeCreate()
          resetForm()
          setAnnounceGoalTarget(newGoal)
        },
        onError: (err) => setError(err instanceof Error ? err.message : t('common.error')),
      })
    }
  }, [
    name, targetValue, unit, user, icon, category, description, year,
    startValue, direction, goalType, steps, autoMilestones, weeklyMilestones,
    manualTargets, isPrivate, monthlyReminder, deadline,
    isEditMode, editingGoal, createGoal, updateGoal, closeCreate, resetForm, t,
  ])

  const isPending = createGoal.isPending || updateGoal.isPending

  return (
    <>
    <GoalAnnounceModal
      isOpen={!!announceGoalTarget}
      isPending={announceGoalMutation.isPending}
      onYes={() => {
        if (announceGoalTarget) {
          announceGoalMutation.mutate(announceGoalTarget, {
            onSettled: () => setAnnounceGoalTarget(null),
          })
        }
      }}
      onNo={() => setAnnounceGoalTarget(null)}
    />
    <AnimatePresence>
      {isCreateSheetOpen && (
        <div className="fixed inset-0 z-[60] flex items-end lg:items-center lg:justify-center pointer-events-none">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto"
            onClick={handleClose}
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%', opacity: 0.5 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full lg:max-w-lg max-h-[90vh] lg:max-h-[85vh] rounded-t-2xl lg:rounded-2xl bg-[var(--color-bg)] border-t border-white/[0.08] lg:border overflow-hidden pointer-events-auto flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] shrink-0">
              <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/[0.06] tap-highlight-transparent">
                <X size={18} className="text-text-hint" />
              </button>
              <h3 className="text-sm font-display font-bold text-text">
                {t('goals.detail')}
              </h3>
              <button
                onClick={handleSubmit}
                disabled={isPending}
                className="px-3.5 py-1.5 rounded-full bg-primary text-white text-xs font-semibold disabled:opacity-50 transition-opacity tap-highlight-transparent"
              >
                {isPending ? '...' : (isEditMode ? t('common.save') : t('goals.createGoal'))}
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto overscroll-y-contain p-4 pb-safe space-y-4">
              {/* Goal type selector */}
              <GoalFormType
                goalType={goalType}
                setGoalType={setGoalType}
                steps={steps}
                setSteps={setSteps}
              />

              <GoalFormBasic
                icon={icon}
                name={name}
                category={category}
                year={year}
                targetValue={targetValue}
                unit={unit}
                direction={direction}
                isPrivate={isPrivate}
                onIconChange={setIcon}
                onNameChange={setName}
                onCategoryChange={setCategory}
                onYearChange={setYear}
                onTargetValueChange={setTargetValue}
                onUnitChange={setUnit}
                onDirectionChange={setDirection}
                onPrivateChange={setIsPrivate}
                hideNumericFields={goalType === 'steps'}
              />

              {/* Advanced sections (hidden for steps) */}
              {goalType === 'numeric' && (
                <div className="pt-2 space-y-2">
                  <p className="text-[10px] font-medium text-text-hint uppercase tracking-wider">
                    {t('goals.advanced')}
                  </p>

                  <AccordionSection
                    title={t('goals.milestones')}
                    icon={<Milestone size={16} />}
                    isOpen={openSection === 'milestones'}
                    onToggle={() => toggleSection('milestones')}
                  >
                    <GoalFormMilestones
                      autoMilestones={autoMilestones}
                      weeklyMilestones={weeklyMilestones}
                      manualTargets={manualTargets}
                      targetValue={Number(targetValue) || 0}
                      onAutoChange={setAutoMilestones}
                      onWeeklyChange={setWeeklyMilestones}
                      onManualTargetChange={handleManualTargetChange}
                    />
                  </AccordionSection>

                  <AccordionSection
                    title={t('goals.extras')}
                    icon={<Settings2 size={16} />}
                    isOpen={openSection === 'advanced'}
                    onToggle={() => toggleSection('advanced')}
                  >
                    <GoalFormAdvanced
                      description={description}
                      startValue={startValue}
                      deadline={deadline}
                      monthlyReminder={monthlyReminder}
                      direction={direction}
                      onDescriptionChange={setDescription}
                      onStartValueChange={setStartValue}
                      onDeadlineChange={setDeadline}
                      onReminderChange={setMonthlyReminder}
                    />
                  </AccordionSection>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="px-3 py-2 rounded-xl bg-danger/[0.1] border border-danger/20">
                  <p className="text-xs text-danger">{error}</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
    </>
  )
}
